import React from "react";
import { LeadCard, Sec, NextPointer, PendingQuoteLine } from "./parts";
import { decidingView, conversationView, whenLine, subjectLine } from "./leadModel.mjs";
import { owedOf } from "../nextModel.mjs";
import { shortDate, plural } from "../format.mjs";
import { gmailThreadUrl, quoteSourceWords, pendingQuote } from "../../timelineModel.mjs";

/*
 * DECIDING INTERNALLY (they answered the pricing): what we quoted and their reply
 * to it, the days since, anything owed and the next meeting (the brief's row;
 * matrix 1e: "Quote and their reply · days since · owed · next meeting"). Undrawn
 * as a frame, so built in the drawn card grammar on Gabriel's ruling (2026-10-01).
 *
 * The latest conversation sits INSIDE this block (the matrix: "Inside the lead
 * block"), so their newest message since the quote is quoted here at reading size
 * with its Open in Gmail ↗, and the shell does not repeat it as a section.
 *
 * THE NO-QUOTE FALLBACK. Older records reached this stage before Pricing sent
 * existed, so their pricing email is often not on the timeline at all. Then the
 * block leads with the days since the call (the brief), says no pricing email is on
 * record, and still quotes their latest message when there is one.
 *
 * Props:
 *   record   the shell's bundle: timeline.rows, act.facts (last_meeting_at,
 *            next_meeting_at, owed, stage_entered_at), company, contact, zones
 *   ops      unused (no actions here: Next owns them)
 */
export default function Deciding({ record }) {
  const rows = record.timeline.rows;
  const facts = (record.act && record.act.facts) || {};
  const v = decidingView({ rows, facts, stageAt: facts.stage_entered_at, company: record.company, now: record.now });
  const waiting = pendingQuote(record.act && record.act.pending);
  const owed = owedOf(facts);
  // A meeting today or tomorrow is already the status line above the block
  // (LeadBlock's MeetingLine), so it is not said twice here.
  const nextAt = facts.next_meeting_at && !facts.next_meeting_declined && !(record.next && record.next.meeting) ? facts.next_meeting_at : null;
  const d = (ts) => shortDate(ts, { now: record.now });
  // Their message, read the way Latest conversation reads it (who in full, their
  // title, their zone), restricted to after the quote by decidingView.
  const convo = v.reply ? conversationView([v.reply], { company: record.company, contact: record.contact, zones: record.zones, now: record.now }) : null;
  const meta = v.since === null || v.since === undefined ? null : `${plural(v.since, "day")} since ${v.sinceWhat}`;

  return (
    <LeadCard label="Deciding internally" meta={v.quote ? meta : null}>
      {v.quote ? (
        <div>
          <div className="rec-lead__small">What we quoted · {lowerFirst(quoteSourceWords(v.quote, d))}</div>
          <div className="rec-strong">{v.quote.terms.join(" · ")}</div>
          <PendingQuoteLine pending={waiting} d={d} />
        </div>
      ) : (
        <div>
          <div className="rec-headline">{v.headline}</div>
          <div className="rec-body">No pricing email on record.</div>
          <PendingQuoteLine pending={waiting} d={d} />
        </div>
      )}
      {convo && (
        <Sec>
          <span className="rec-lead__small">
            {convo.lead.who}
            {convo.lead.title ? ` · ${convo.lead.title}` : ""} · {convo.lead.when}
            {v.quote ? " · their reply" : ""}
          </span>
          {convo.lead.body && <div className="rec-quote rec-quote--md">{convo.lead.body}</div>}
          <span className="rec-meta">
            {subjectLine(convo)}
            {gmailThreadUrl(convo.lead.thread_id) && (
              <>
                {" · "}
                <a href={gmailThreadUrl(convo.lead.thread_id)} target="_blank" rel="noopener noreferrer">Open in Gmail ↗</a>
              </>
            )}
          </span>
        </Sec>
      )}
      {(owed || nextAt) && (
        <Sec>
          {owed && (
            <span className="rec-lead__ink">
              <b>Owed:</b> {owed.what} <NextPointer />
            </span>
          )}
          {nextAt && (
            <span className="rec-lead__ink">
              <b>Next meeting:</b> {whenLine(nextAt, record.zones, { now: record.now })}
              {facts.next_meeting_title ? ` · ${facts.next_meeting_title}` : ""}
            </span>
          )}
        </Sec>
      )}
    </LeadCard>
  );
}

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
